// Éditeur des champs CEE OMV DANS la fiche dossier (relocalisé depuis la création).
// Field-names = spec SPEC-CEE-OMV-DOSSIER-2026-06-07 (schéma models.py, DossierUpdate).
// Gating : rendu uniquement pour les dossiers tenant=omv_france (cf. appelant).
import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Save, Loader2, Search, Upload } from "lucide-react";
import { useConfigList } from "../../hooks/useConfigList";
import { isLuhnValid, useSiretSearch } from "../../hooks/useSiretSearch";
import { normalizeIdentifier } from "../../lib/normalize";
import { CEE_IMPORT_MAP } from "../../utils/ceeImportMap";
import { applyDetectedFormRef } from "../../lib/omvDossier";
import BanAddressInput from "../shared/BanAddressInput";   // SOCLE partagé (CC-DEV1-31)

// Extrait un code postal (5 chiffres) d'une adresse libre.
const extractCP = (s) => (String(s || "").match(/\b(\d{5})\b/) || [])[1] || "";

const API = process.env.REACT_APP_BACKEND_URL || "";


// Inputs au NIVEAU MODULE (identité stable → pas de perte de focus).
function Field({ label, value, onChange, type = "text", placeholder, error }) {
  return (
    <div>
      <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "À remplir"}
        className="w-full rounded px-3 py-2 text-sm"
        aria-invalid={!!error}
        style={{ background: "var(--oh-bg-surface)", border: `1px solid ${error ? "var(--oh-stat-red-border)" : "var(--oh-border)"}`, color: "var(--oh-text)" }} />
      {error && <p className="text-xs mt-1" style={{ color: "var(--oh-stat-red-text)" }}>{error}</p>}
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>{label}</label>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded px-3 py-2 text-sm"
        style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}>
        <option value="">À remplir</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// VMC affichée seulement pour les fiches VMC (TH-125 / TH-126), quel que soit le
// préfixe (BAR-/BAT-). Masquée sinon (EN-103, etc.).
const isVmcFiche = (code) => /TH-12[56]/i.test(String(code || ""));

// ── Chantier F / P-052 — défaut intelligent du secteur d'OPÉRATION CEE ────────
// Normalisation tolérante (accents + casse) pour comparer secteurs et noms.
const _normSecteur = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
// Mots-clés « EHPAD » cherchés dans le NOM du bénéficiaire (raison sociale).
const _EHPAD_KEYWORDS = ["ehpad", "ephad", "maison de retraite", "residence"];
// Décision JJ (P-052) — défaut de secteur CEE d'une opération d'après le bénéficiaire
// (secteur_activite + raison_sociale) et les options RÉELLES de la fiche :
//  1. secteur_activite ~ « Santé » ET nom ~ EHPAD/EPHAD/maison de retraite/résidence → « EHPAD »
//  2. secteur_activite ~ « Santé » sans ces mots → « Santé »
//  3. autre secteur_activite → option de fiche correspondante si match simple, sinon null
//  4. « Résidentiel » : hors scope (non géré ici) · 5. pas de secteur_activite → null
// Fonction PURE (testable hors React). Retourne la valeur à pré-remplir (string) ou null.
function computeSecteurDefaut(secteurActivite, raisonSociale, options) {
  const sa = _normSecteur(secteurActivite);
  if (!sa) return null;                                      // (5)
  if (sa === "residentiel") return null;                     // (4) hors scope (décision JJ)
  const opts = Array.isArray(options) ? options : [];
  const findOpt = (pred) => opts.find(pred) || null;
  if (sa.includes("sant")) {
    const nom = _normSecteur(raisonSociale);
    if (_EHPAD_KEYWORDS.some((k) => nom.includes(k))) {      // (1) EHPAD par le nom
      return findOpt((o) => _normSecteur(o) === "ehpad") || "EHPAD";
    }
    return findOpt((o) => _normSecteur(o).includes("sant")); // (2) Santé
  }
  // (3) autre secteur : correspondance simple avec une option (exacte ou membre d'une clé groupée "a,b,c")
  return findOpt((o) => _normSecteur(o) === sa)
      || findOpt((o) => String(o).split(",").map(_normSecteur).includes(sa))
      || null;
}

// CC-DEV1-26 — STRUCTURE par opération (onglet) : Fiche CEE + Surface + Secteur
// (+ Nb caissons UNIQUEMENT si fiche VMC 125/126). Délégataire = niveau GLOBAL
// (commun à toutes les opérations) ; Zone = niveau DOSSIER (déduite adresse travaux).
// Plus de sélecteur simple/double redondant dans l'opération (la fiche le porte) ni
// de calcul auto de caissons. Inputs inline (identité stable, pas de perte de focus).
function OperationFields({ codeFiche, onFiche, secteur, onSecteur, surface, onSurface, nbCaissons, onNbCaissons, ficheOptions, secteurOptions }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <SelectField label="Fiche CEE" value={codeFiche || ""} onChange={onFiche} options={ficheOptions} />
      <SelectField label="Secteur (opération)" value={secteur || ""} onChange={onSecteur} options={secteurOptions} />
      <Field label="Surface (m²)" type="number" value={surface} onChange={onSurface} />
      {/* Nb caissons : CONDITIONNEL — seulement pour les fiches VMC (125/126). */}
      {isVmcFiche(codeFiche) && (
        <Field label="Nb caissons" type="number" value={nbCaissons ?? ""} onChange={onNbCaissons} />
      )}
    </div>
  );
}

// Réutilisable dans 2 contextes (mêmes champs/listes/structure) :
//  • fiche DOSSIER (défaut) → save = PATCH /dossiers/{id} (inchangé).
//  • formulaire PROSPECT (D4) → passer `onSubmit(body)` (async→bool) : le composant
//    construit le même `body` {entreprise,representant,batiment,technique,cee} et
//    délègue l'enregistrement au parent (POST/PATCH /prospects). `dossier` reçoit
//    alors le `data{}` du prospect (même forme). `submitLabel` = libellé du bouton.
//  • formulaire PROSPECT PUBLIC (sans login) → `publicMode` + `publicSlug` : les
//    LISTES (secteurs/fiches/délégataires/zone) sont chargées via l'endpoint PUBLIC
//    signé/allowlisté `/api/d4/config-lists` (pas useConfigList ni /aides/* qui
//    exigent un Bearer) → même composant, mêmes listes, sans auth.
// `checkDuplicate(siretDigits)` (optionnel, contexte PROSPECT) → renvoie un objet
//   {exists, kind, soft, masked_ref} pour afficher un AVERTISSEMENT doublon NON
//   bloquant sous le SIRET. Absent (fiche dossier) → aucun check.
export default function OmvCeeEditor({ dossier, dossierId, tenantId, onUpdate, readOnly = false, onSubmit, submitLabel, publicMode = false, publicSlug, checkDuplicate }) {
  const [form, setForm] = useState(() => ({
    entreprise: { raison_sociale: "", secteur_activite: "", siret: "", adresse: "", cp: "", ville: "", ...(dossier?.entreprise || {}) },
    representant: { nom: "", prenom: "", fonction: "", email: "", tel: "", portable: "", ...(dossier?.representant || {}) },
    batiment: { type_batiment: "", plus_2_ans: false, surface_ventilee_m2: "", nb_logements: "", adresse_travaux: "", cp_travaux: "", ville_travaux: "", nom_etablissement: "", ...(dossier?.batiment || {}) },
    technique: (() => {
      const t = { type_vmc: [], nb_caissons: "", qte_simple_flux: "", qte_double_flux: "", ...(dossier?.technique || {}) };
      // Rétro-compat : fiches saisies en mono (str) → tableau.
      t.type_vmc = Array.isArray(t.type_vmc) ? t.type_vmc : (t.type_vmc ? [t.type_vmc] : []);
      // Best-effort reload : quantités par type absentes mais nb_caissons + UN seul
      // type connu → pré-remplir ce type (le détail par type n'est pas encore persisté
      // côté back — chantier multi-VMC suivant).
      if (t.qte_simple_flux === "" && t.qte_double_flux === "" && t.nb_caissons && t.type_vmc.length === 1) {
        if (t.type_vmc[0] === "simple_flux") t.qte_simple_flux = t.nb_caissons;
        else if (t.type_vmc[0] === "double_flux") t.qte_double_flux = t.nb_caissons;
      }
      return t;
    })(),
    cee: { code_fiche: "", operation_cee: "", secteur: "", zone_climatique: "", montant_prime_cee: "", delegataire_nom: "", volume_kwh_cumac: "", reference_formulaire: "", ...(dossier?.cee || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const setF = (section, key, val) => setForm((f) => ({ ...f, [section]: { ...f[section], [key]: val } }));

  // ── SIRET/SIREN en tête + recherche SIRENE (API publique gouv, EXTERNE → pas
  //    d'auth/allowlist). Auto-remplit l'entreprise (UNIQUEMENT les champs vides →
  //    pas d'écrasement d'une fiche dossier existante) + le site si vide. Règle
  //    siège/établissement via mapEntreprise (est_siege). SIREN dérivé (affiché).
  const { info: siretInfo, searchDebounced: searchSiret } = useSiretSearch();
  const [siretMsg, setSiretMsg] = useState("");
  const [dupWarn, setDupWarn] = useState(null);   // {exists, prospect, dossier} | null (dédup séparée par monde)
  const runDuplicateCheck = (digits) => {
    if (!checkDuplicate) return;
    const d = (digits || "").replace(/\D/g, "");
    if (d.length !== 9 && d.length !== 14) { setDupWarn(null); return; }
    Promise.resolve(checkDuplicate(d)).then((r) => setDupWarn(r && r.exists ? r : null)).catch(() => setDupWarn(null));
  };
  const onSiretChange = (val) => {
    // #314 — moteur unique de normalisation à la saisie.
    const clean = normalizeIdentifier(val, "siret");
    setF("entreprise", "siret", clean);
    if ((clean.length === 9 || clean.length === 14) && !isLuhnValid(clean)) {
      setSiretMsg("SIRET/SIREN invalide (clé de contrôle) — vérifiez le numéro.");
    } else {
      setSiretMsg("");
    }
    runDuplicateCheck(clean);
    searchSiret(clean, (m) => {
      if (!m) return;
      setForm((f) => ({
        ...f,
        entreprise: {
          ...f.entreprise,
          siret: m.siret || clean,
          raison_sociale: f.entreprise.raison_sociale || m.raison_sociale || "",
          // siège vs établissement : si l'établissement interrogé n'est pas le siège,
          // l'adresse bénéficiaire = celle du siège (sinon l'établissement).
          adresse: f.entreprise.adresse || (m.est_siege ? m.rue : (m.siege_rue || m.rue)) || "",
          cp: f.entreprise.cp || (m.est_siege ? m.code_postal : (m.siege_cp || m.code_postal)) || "",
          ville: f.entreprise.ville || (m.est_siege ? m.ville : (m.siege_ville || m.ville)) || "",
        },
        batiment: {
          ...f.batiment,
          nom_etablissement: f.batiment.nom_etablissement || m.nom_etablissement || "",
          adresse_travaux: f.batiment.adresse_travaux || m.rue || "",
          cp_travaux: f.batiment.cp_travaux || m.code_postal || "",
          ville_travaux: f.batiment.ville_travaux || m.ville || "",
        },
      }));
      runDuplicateCheck(m.siret || clean);
    });
  };
  // SIREN dérivé (9 premiers chiffres) pour affichage.
  const sirenDerive = (form.entreprise.siret || "").replace(/\D/g, "").slice(0, 9);

  // ── Import formulaire CEE (Phase 1) ───────────────────────────────────────
  // Upload PDF → POST /api/metier/omv/cee/extract-form (EXTRACTION PURE, gate
  // omv_france, AUCUNE écriture dossier/séquence/Zoho) → pré-remplit le formulaire
  // prospect SANS écraser une saisie, en réutilisant CEE_IMPORT_MAP (source unique).
  // Disponible seulement en interne connecté (régie incluse) : en publicMode il n'y
  // a pas de session → l'endpoint (require_tenant_scope) renverrait 401.
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);  // [{label, value}] champs extraits
  // Fichier formulaire importé, retenu pour l'attacher au prospect à la sauvegarde
  // (le parent reçoit ce File en 2e arg de onSubmit → POST /prospects/{id}/attach-form).
  const [importedFile, setImportedFile] = useState(null);

  const applyCeeExtraction = (data) => {
    // Anti-duplication CP : Vision met parfois l'adresse COMPLÈTE dans
    // batiment_adresse_travaux → on découpe rue / cp / ville (pas de CP répété).
    let rue = String(data.batiment_adresse_travaux || "");
    let cp = data.batiment_cp_travaux ? String(data.batiment_cp_travaux) : "";
    let ville = data.batiment_ville_travaux ? String(data.batiment_ville_travaux) : "";
    const cpInAdr = (rue.match(/\b(\d{5})\b/) || [])[1] || "";
    if (!cp && cpInAdr) cp = cpInAdr;
    if (!ville && cpInAdr) ville = (rue.split(cpInAdr)[1] || "").replace(/^[\s,;-]+/, "").trim();
    if (cp) rue = rue.replace(new RegExp(`\\b${cp}\\b`, "g"), " ");
    if (ville) rue = rue.replace(new RegExp(ville.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig"), " ");
    rue = rue.replace(/[,;]/g, " ").replace(/\s{2,}/g, " ").replace(/^[\s,;-]+|[\s,;-]+$/g, "").trim();
    data.batiment_adresse_travaux = rue || null;
    data.batiment_cp_travaux = cp || null;
    data.batiment_ville_travaux = ville || null;

    // Entreprise via SIRET détecté → enrichissement SIRENE (API EXTERNE, aucune
    // écriture) + dédup prospect non bloquante (réutilise onSiretChange).
    if (data.siret_detecte) onSiretChange(data.siret_detecte);

    let count = 0;
    setForm((f) => {
      const next = {
        ...f,
        representant: { ...f.representant },
        batiment: { ...f.batiment },
        technique: { ...f.technique },
        cee: { ...f.cee },
      };
      for (const [key, section, field, , isBool] of CEE_IMPORT_MAP) {
        const v = data[key];
        if (v === null || v === undefined || v === "") continue;
        const cur = next[section][field];
        if (Array.isArray(cur)) continue;  // type_vmc = multi-select (toggles) → traité à part ci-dessous
        const empty = isBool ? (cur === false || cur === "" || cur == null) : (cur === "" || cur == null);
        if (!empty) continue;              // ne jamais écraser une saisie existante
        next[section][field] = isBool ? !!v : String(v);
        count++;
      }

      // VMC type extrait → technique.type_vmc (compat grille/devis) SI vide. Le détail
      // (caissons, fiche par op) part désormais dans `operations` (synchro ci-dessous).
      const vmc = data.technique_type_vmc;          // "simple_flux" | "double_flux" | null
      const curTypes = Array.isArray(next.technique.type_vmc) ? next.technique.type_vmc : [];
      if (vmc && curTypes.length === 0) { next.technique.type_vmc = [vmc]; count++; }
      // Réf. formulaire CEE détectée (Vision) → cee.reference_formulaire (NON-
      // identifiant, jamais le numéro de devis). MÊME helper que CreateDossier
      // (applyDetectedFormRef) → pré-remplissage IDENTIQUE dans les 2 écrans.
      const beforeRef = next.cee.reference_formulaire;
      next.cee = applyDetectedFormRef(next.cee, data.numero_dossier_detecte);
      if (next.cee.reference_formulaire && next.cee.reference_formulaire !== beforeRef) count++;
      return next;
    });
    // Multi-op (CC-DEV1-26) : opérations détectées (VMC simple→BAT-TH-125, double→BAT-TH-126,
    // LES DEUX → 2 opérations). IA pré-remplit le MAX : fiche(s) + surface + nb caissons
    // (depuis le formulaire) ; le secteur reste à compléter par Solène. N'écrase PAS des
    // opérations déjà renseignées.
    const detected = Array.isArray(data.operations_detectees) ? data.operations_detectees : [];
    const codes = detected.map((o) => o.fiche_ref).filter(Boolean);
    const codesFinal = codes.length ? codes : (data.operation_code_fiche ? [data.operation_code_fiche] : []);
    if (codesFinal.length) {
      const surf = (data.batiment_surface_ventilee_m2 != null && data.batiment_surface_ventilee_m2 !== "") ? String(data.batiment_surface_ventilee_m2) : "";
      const nbc = (data.technique_nb_caissons != null && data.technique_nb_caissons !== "") ? String(data.technique_nb_caissons) : "";
      setOperations((cur) => {
        if (cur.some((o) => o.fiche_ref)) return cur;   // ne pas écraser une saisie existante
        return codesFinal.map((code, i) => ({
          fiche_ref: code, secteur: "",
          surface: i === 0 ? surf : "",
          nb_caissons: (i === 0 && isVmcFiche(code)) ? nbc : "",
        }));
      });
    }
    // APERÇU "à confirmer" — liste des champs EXTRAITS, INDÉPENDANTE du rendu
    // conditionnel (VMC/fiche). JJ/Solène voient ce qui a été importé même si un
    // champ est masqué. (Calculé depuis l'extraction brute, pas l'état.)
    const preview = [];
    if (data.siret_detecte) preview.push({ label: "SIRET → SIREN", value: String(data.siret_detecte) });
    for (const [key, , , label] of CEE_IMPORT_MAP) {
      const v = data[key];
      if (v === null || v === undefined || v === "") continue;
      preview.push({ label, value: typeof v === "boolean" ? (v ? "Oui" : "Non") : String(v) });
    }
    if (data.numero_dossier_detecte) preview.push({ label: "Réf. formulaire (CEE)", value: String(data.numero_dossier_detecte) });
    if (detected.length > 1) preview.push({ label: "Opérations détectées (VMC)", value: detected.map((o) => o.fiche_ref).join(" + ") });
    setImportPreview(preview);
    toast.success(`Formulaire CEE importé — ${count} champ(s) pré-rempli(s) à vérifier.`);
  };

  const handleImportForm = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const token = localStorage.getItem("session_token");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/metier/omv/cee/extract-form`, {
        method: "POST",
        credentials: "include",
        // Pas de Content-Type : le navigateur pose le boundary multipart.
        headers: {
          "X-Tenant-ID": tenantId || localStorage.getItem("current_tenant_slug") || "",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: fd,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        const msg = typeof e?.detail === "string" ? e.detail : `Extraction du formulaire échouée (${res.status}).`;
        toast.error(msg);
        return;
      }
      const data = await res.json();
      // Retenir le fichier importé pour l'ATTACHER au prospect à l'enregistrement
      // (Brique 1, contexte prospect). Conservé même si extraction limitée (scan).
      setImportedFile(file);
      if (data?._meta?.source === "needs_vision") {
        toast.message("Document scanné — extraction limitée, complétez à la main.");
        return;
      }
      applyCeeExtraction(data);
    } catch (err) {
      toast.error("Erreur réseau pendant l'import du formulaire.");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Opérations CEE — LISTE UNIFIÉE (CC-DEV1-26) ────────────────────────────
  // UN seul tableau `operations` (fin de la scission cee/extraOps) : chaque op =
  // {fiche_ref, secteur, surface, nb_caissons}. cumac/prime = CALCULÉS SERVEUR
  // (compute + recalcul update_dossier, source unique). Init = dossier.operations[]
  // sinon dérivé de cee/batiment/technique (compat dossiers/prospects existants).
  // Le composant est PARTAGÉ (prospect + éditeur dossier) → la correction vaut pour les deux.
  const _emptyOp = () => ({ fiche_ref: "", secteur: "", surface: "", nb_caissons: "", produit_id: "", produit_ref: "", prix_mode: "cee" });
  const [operations, setOperations] = useState(() => {
    const src = dossier?.operations || [];
    if (src.length) return src.map((o) => ({
      fiche_ref: o.fiche_ref || o.code_fiche || "", secteur: o.secteur || "",
      surface: o.surface != null ? String(o.surface) : "",
      nb_caissons: o.nb_caissons != null ? String(o.nb_caissons) : "",
      produit_id: o.produit_id || "", produit_ref: o.produit_ref || "", prix_mode: o.prix_mode || "cee",
      commentaire_ligne: o.commentaire_ligne || "",
    }));
    const cee = dossier?.cee || {}, bat = dossier?.batiment || {}, tech = dossier?.technique || {};
    if (cee.code_fiche || bat.surface_ventilee_m2) return [{
      fiche_ref: cee.code_fiche || "", secteur: cee.secteur || "",
      surface: bat.surface_ventilee_m2 != null ? String(bat.surface_ventilee_m2) : "",
      nb_caissons: tech.nb_caissons != null ? String(tech.nb_caissons) : "",
      produit_id: "", produit_ref: "", prix_mode: "cee",
    }];
    return [_emptyOp()];
  });
  const [activeOp, setActiveOp] = useState(0);
  const [opsCalc, setOpsCalc] = useState(null);   // réponse /operations/compute : {operations:[{cumac,prime}], totals}
  const setOp = (idx, key, val) => setOperations((o) => o.map((op, k) => (k === idx ? { ...op, [key]: val } : op)));
  const addOp = () => { setOperations((o) => [...o, _emptyOp()]); setActiveOp(operations.length); };
  const removeOp = (idx) => setOperations((o) => {
    const n = o.filter((_, k) => k !== idx);
    setActiveOp(0);
    return n.length ? n : [_emptyOp()];
  });

  // Sélecteur Type VMC GLOBAL — PROSPECT uniquement (la régie coche simple/double/les 2 →
  // pilote la création des opérations : simple→BAT-TH-125, double→BAT-TH-126). Retiré de
  // l'éditeur DOSSIER (la fiche 125/126 porte déjà l'info). Toggle = ajoute/retire l'op.
  const isProspect = !!onSubmit;
  const VMC_FOR_TYPE = { simple_flux: "BAT-TH-125", double_flux: "BAT-TH-126" };
  const toggleVmcGlobal = (type) => {
    const code = VMC_FOR_TYPE[type];
    setOperations((o) => {
      if (o.some((x) => x.fiche_ref === code)) {
        const n = o.filter((x) => x.fiche_ref !== code);
        return n.length ? n : [_emptyOp()];
      }
      const emptyIdx = o.findIndex((x) => !x.fiche_ref);
      if (emptyIdx >= 0) return o.map((x, k) => (k === emptyIdx ? { ...x, fiche_ref: code } : x));
      return [...o, { ..._emptyOp(), fiche_ref: code }];
    });
  };

  // Listes (source unique base) : fiches CEE + délégataires + PRODUITS catalogue du tenant.
  const [fiches, setFiches] = useState([]);
  const [delegataires, setDelegataires] = useState([]);
  const [produits, setProduits] = useState([]);   // CC-DEV1-28 — pour la liaison produit↔opération
  // Secteur d'activité bénéficiaire : LISTE du Module Aides (Variables et Listes), pas libre.
  // En mode PUBLIC, useConfigList échoue (pas de Bearer) → on prend la liste servie
  // par l'endpoint public ci-dessous. Le hook est appelé inconditionnellement (règle
  // des hooks) mais son résultat est ignoré en public.
  const { items: secteursAuth } = useConfigList("secteurs_activite");
  const [secteursPublic, setSecteursPublic] = useState([]);
  const secteursActivite = publicMode ? secteursPublic : secteursAuth;

  // Zone climatique CALCULÉE depuis l'ADRESSE : priorité adresse des TRAVAUX (si remplie) →
  // sinon adresse PRINCIPALE (bénéficiaire). Pas de forçage Hx manuel comme mécanisme principal.
  const [zoneDeduite, setZoneDeduite] = useState("");
  const cpTravaux = (form.batiment?.cp_travaux || "").trim() || extractCP(form.batiment?.adresse_travaux);
  const cpPrincipal = form.entreprise?.cp || "";
  const cpForZone = cpTravaux || cpPrincipal;
  const zoneSource = cpTravaux ? "adresse des travaux" : (cpPrincipal ? "adresse principale" : "");
  // Re-sync du Volume kWh cumac quand le dossier change (calculé/appliqué côté wizard) :
  // l'initialiseur useState ne s'exécute qu'au montage → sinon le champ reste vide.
  useEffect(() => {
    const v = dossier?.cee?.volume_kwh_cumac;
    if (v !== undefined && v !== null) setF("cee", "volume_kwh_cumac", v);
  }, [dossier?.cee?.volume_kwh_cumac]);
  useEffect(() => {
    const cp = (cpForZone || "").trim();
    if (!/^\d{4,5}$/.test(cp)) { setZoneDeduite(""); return; }
    // Public : endpoint signé/allowlisté sans auth. Authentifié : /aides/zones avec Bearer.
    const url = publicMode
      ? `${API}/api/d4/zone/${cp}?tenant=${encodeURIComponent(publicSlug || "")}`
      : `${API}/api/metier/aides/zones/${cp}`;
    const opts = publicMode
      ? {}
      : { headers: { "X-Tenant-ID": tenantId || "omv_france", Authorization: `Bearer ${localStorage.getItem("session_token")}` }, credentials: "include" };
    (async () => {
      try { const r = await fetch(url, opts); if (r.ok) { const d = await r.json(); setZoneDeduite(d.zone || d.zone_climatique || ""); } } catch { /* noop */ }
    })();
  }, [cpForZone, tenantId, publicMode, publicSlug]);

  // ── Multi-op (CC-DEV1-26) : payload depuis la LISTE unifiée. Zone = DOSSIER (même
  // valeur pour toutes), nb_caissons seulement si VMC. cumac/prime recalculés serveur.
  const opNum = (v) => (v === "" || v == null ? null : (parseFloat(String(v).replace(",", ".")) || null));
  const opInt = (v) => (v === "" || v == null ? null : (parseInt(String(v), 10) || null));
  const buildOperations = () => operations.filter((o) => o.fiche_ref).map((o) => ({
    fiche_ref: o.fiche_ref,
    secteur: o.secteur || null,
    surface: opNum(o.surface),
    zone: zoneDeduite || form.cee.zone_climatique || null,
    nb_caissons: isVmcFiche(o.fiche_ref) ? opInt(o.nb_caissons) : null,
    delegataire_nom: form.cee.delegataire_nom || null,
    // Liaison produit↔opération + mode prix (CC-DEV1-28).
    produit_id: o.produit_id || null,
    produit_ref: o.produit_ref || null,
    prix_mode: o.prix_mode || "cee",
    // N°980 — commentaire de la ligne de devis issue de CETTE opération. Envoyé
    // en chaîne ("" = effacé) : l'opération est la source que relit la
    // reconstruction du devis. ⛔ Jamais copié au catalogue.
    commentaire_ligne: (o.commentaire_ligne || "").trim(),
  }));
  // Secteurs proposés = clés facteur_secteur de la fiche choisie (secteurs NON universels).
  const secteursForFiche = (code) => {
    const f = (fiches || []).find((x) => (x.code || x.fiche_ref) === code);
    return f && f.facteur_secteur ? Object.keys(f.facteur_secteur) : [];
  };
  // Options du sélecteur de fiche (+ repli si une valeur importée n'est pas dans la liste).
  const ficheOptionsFor = (code) => {
    const opts = (fiches || []).map((f) => ({ value: f.code || f.fiche_ref, label: `${f.code || f.fiche_ref}${(f.libelle || f.nom) ? ` — ${f.libelle || f.nom}` : ""}` }));
    if (code && !opts.some((o) => o.value === code)) opts.unshift({ value: code, label: `${code} (importé)` });
    return opts;
  };

  // Options du secteur d'opération = clés facteur_secteur de la fiche + REPLI : inclut la
  // valeur courante si absente (ex. « EHPAD » pré-sélectionné P-052 ou valeur importée),
  // pour qu'elle reste visible/sélectionnée dans le <select> natif.
  const secteurOptionsForOp = (op) => {
    const opts = secteursForFiche(op.fiche_ref).map((s) => ({ value: s, label: s }));
    const cur = String(op.secteur ?? "").trim();
    if (cur && !opts.some((o) => o.value === cur)) opts.unshift({ value: cur, label: cur });
    return opts;
  };

  // ── Chantier F / P-052 — application du défaut de secteur (voir computeSecteurDefaut) ──
  // Défaut MODIFIABLE : n'écrase JAMAIS un secteur déjà saisi (uniquement les vides), ne
  // se réapplique pas après un choix (garde par opération). Bénéficiaire sans
  // secteur_activite, ou « Résidentiel » → aucun défaut (hors scope JJ).
  const secteurDefaultDone = useRef(new Set());   // index d'opérations déjà auto-défautées
  useEffect(() => {
    const fills = [];
    operations.forEach((op, i) => {
      if (secteurDefaultDone.current.has(i)) return;
      if (!op.fiche_ref || String(op.secteur ?? "").trim() !== "") return;  // fiche requise + secteur VIDE only
      const def = computeSecteurDefaut(form.entreprise.secteur_activite, form.entreprise.raison_sociale, secteursForFiche(op.fiche_ref));
      if (def) fills.push([i, def]);
    });
    if (!fills.length) return;
    fills.forEach(([i]) => secteurDefaultDone.current.add(i));
    setOperations((ops) => ops.map((op, i) => {
      const hit = fills.find(([k]) => k === i);
      return hit && !String(op.secteur ?? "").trim() ? { ...op, secteur: hit[1] } : op;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_normSecteur(form.entreprise.secteur_activite), _normSecteur(form.entreprise.raison_sociale), JSON.stringify(operations.map((o) => [o.fiche_ref, o.secteur])), fiches]);
  // VALIDATION (UX JJ) : une op complète = fiche + secteur + surface (+ nb caissons si VMC).
  const opComplete = (o) => !!o.fiche_ref && !!o.secteur && String(o.surface ?? "").trim() !== ""
    && (!isVmcFiche(o.fiche_ref) || String(o.nb_caissons ?? "").trim() !== "");
  const opsWithFiche = operations.filter((o) => o.fiche_ref);
  const opsEmptyCount = operations.filter((o) => !o.fiche_ref).length;
  const allOpsComplete = opsWithFiche.length > 0 && opsEmptyCount === 0 && opsWithFiche.every(opComplete);
  const validationMsg = !allOpsComplete
    ? (opsWithFiche.length === 0 ? "Ajoutez au moins une opération (choisir une fiche CEE)."
       : opsEmptyCount > 0 ? "Choisissez une fiche CEE pour chaque opération (ou retirez l'onglet vide)."
       : "Complétez chaque opération : surface, secteur" + " (et nb caissons si VMC).")
    : "";
  // Calcul live serveur (cumac+prime par op + total). Skip en mode public (pas de Bearer).
  useEffect(() => {
    if (publicMode) return;
    const t = setTimeout(async () => {
      try {
        const ops = buildOperations();
        if (!ops.length) { setOpsCalc(null); return; }
        const r = await fetch(`${API}/api/metier/omv/operations/compute`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Tenant-ID": tenantId || "omv_france", Authorization: `Bearer ${localStorage.getItem("session_token")}` },
          credentials: "include",
          body: JSON.stringify({ operations: ops, code_postal: (cpForZone || "").trim() }),
        });
        if (r.ok) setOpsCalc(await r.json()); else setOpsCalc(null);
      } catch { /* noop */ }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(operations), zoneDeduite, form.cee.zone_climatique, tenantId, publicMode]);
  const fmtKwh = (v) => (v == null ? "—" : `${Math.round(v).toLocaleString("fr-FR")} kWh cumac`);
  const fmtEur = (v) => (v == null || v === 0 ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
  // REPLI adresse travaux : si VIDE → recopier l'adresse PRINCIPALE (bénéficiaire)
  // quand elle est connue/màj → adresse travaux toujours renseignée → zone calculée
  // → prime juste. Déclenché par le changement de l'adresse PRINCIPALE (pas par
  // l'édition de travaux) : si l'utilisateur efface ensuite, on ne refait pas le repli.
  useEffect(() => {
    setForm((f) => {
      const b = f.batiment, e = f.entreprise;
      const travauxVide = !(b.adresse_travaux || "").trim() && !(b.cp_travaux || "").trim() && !(b.ville_travaux || "").trim();
      const principalRenseignee = (e.adresse || "").trim() || (e.cp || "").trim() || (e.ville || "").trim();
      if (!travauxVide || !principalRenseignee) return f;
      return { ...f, batiment: { ...b, adresse_travaux: e.adresse || "", cp_travaux: e.cp || "", ville_travaux: e.ville || "" } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.entreprise.adresse, form.entreprise.cp, form.entreprise.ville]);
  useEffect(() => {
    (async () => {
      // PUBLIC : un seul endpoint signé/allowlisté sert toutes les listes (secteurs,
      // fiches, délégataires) sans Bearer → même rendu que la fiche dossier.
      if (publicMode) {
        try {
          const r = await fetch(`${API}/api/d4/config-lists?tenant=${encodeURIComponent(publicSlug || "")}`);
          if (r.ok) {
            const d = await r.json();
            setFiches(d.fiches || []);
            setDelegataires(d.delegataires || []);
            setSecteursPublic(d.secteurs_activite || []);
          }
        } catch { /* noop — saisie reste possible, listes vides */ }
        return;
      }
      const h = { "X-Tenant-ID": tenantId || "omv_france", Authorization: `Bearer ${localStorage.getItem("session_token")}` };
      try { const r = await fetch(`${API}/api/metier/aides/fiches-cee`, { headers: h, credentials: "include" }); if (r.ok) setFiches(await r.json()); } catch { /* noop */ }
      // Pas de `noop` ici : sans référentiel délégataires, le SIREN n'est pas vérifiable
      // et la sélection sera refusée — l'utilisateur doit savoir POURQUOI.
      try {
        const r = await fetch(`${API}/api/metier/aides/delegataires`, { headers: h, credentials: "include" });
        if (r.ok) setDelegataires(await r.json());
        else toast.error("Référentiel des délégataires indisponible — le choix du délégataire sera refusé.");
      } catch {
        toast.error("Référentiel des délégataires injoignable — le choix du délégataire sera refusé.");
      }
      // Produits catalogue (CC-DEV1-28) → liaison produit↔opération (filtrage par fiche_cee).
      try { const r = await fetch(`${API}/api/metier/catalogue/produits?limit=200&actif_only=false`, { headers: h, credentials: "include" }); if (r.ok) setProduits(await r.json()); } catch { /* noop */ }
    })();
  }, [tenantId, publicMode, publicSlug]);
  // Produits liés à une fiche (clé : Produit.fiche_cee == operation.fiche_ref).
  // 🔴 `p.actif !== false` — mesuré le 2026-09-02 par CC PROD : ce filtre ne testait
  // QUE la fiche CEE. Or la liste est chargée avec `actif_only=false` (l. 556), donc
  // un produit DÉSACTIVÉ était proposé ici alors qu'il est exclu du DevisWizard.
  // ⛔ L'arbitrage JJ « prix d'attente ⇒ pas sélectionnable en devis » ne tenait pas
  // sur ce chemin : les 12 KANA n'y échappaient que parce que leur `fiche_cee` est
  // nulle — un accident de donnée, ⛔ pas une garantie. Un produit inactif portant
  // une fiche CEE serait sorti sur un devis client.
  const produitsForFiche = (code) =>
    (produits || []).filter((p) => p.actif !== false && p.fiche_cee && p.fiche_cee === code);
  // Délégataire (liste aides_delegataires) → nom + SIREN.
  // INVARIANT (nom, SIREN) : le couple change ENSEMBLE. Le SIREN ne retombe JAMAIS sur
  // celui du délégataire précédent — c'est ainsi que des dossiers ont nommé ACE ENERGIE
  // en affichant le SIREN d'EFFY CERT sur une pièce réglementaire. Si le référentiel
  // n'est pas disponible (liste non chargée, fetch en échec), on REFUSE la sélection
  // et on le dit, plutôt que de poser un SIREN faux en silence.
  const onSelectDeleg = (nom) => {
    const d = (delegataires || []).find(x => (x.nom || "") === nom);
    if (!d) {
      toast.error(
        (delegataires || []).length === 0
          ? "Liste des délégataires indisponible — sélection refusée (le SIREN ne peut pas être vérifié)."
          : `Délégataire « ${nom} » introuvable au référentiel — sélection refusée.`
      );
      return;
    }
    setForm((prev) => ({ ...prev, cee: { ...prev.cee, delegataire_nom: nom, delegataire_siren: d.siren || "" } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => (v === "" || v == null ? null : (parseFloat(String(v).replace(",", ".")) || null));
      const clean = (obj) => {
        const o = {};
        for (const [k, v] of Object.entries(obj)) if (v !== "" && v != null) o[k] = v;
        return Object.keys(o).length ? o : null;
      };
      // CC-DEV1-26 — operations[] = SOURCE des champs. On DÉRIVE cee/technique/batiment de
      // l'op principale (operations[0]) pour la compat : devis OMV (lit cee), grille VMC
      // (technique.type_vmc), templates. cumac/prime recalculés SERVEUR (update_dossier).
      const ops = buildOperations();
      const op0 = ops[0] || {};
      const ficheNom = (code) => { const f = (fiches || []).find((x) => (x.code || x.fiche_ref) === code); return f && (f.libelle || f.nom); };
      const vmcTypes = [...new Set(ops.filter((o) => isVmcFiche(o.fiche_ref))
        .map((o) => (/TH-125/i.test(o.fiche_ref) ? "simple_flux" : "double_flux")))];
      const body = {
        // numero_dossier n'est plus saisi en UI (numéro auto = dossier.reference).
        entreprise: clean(form.entreprise),
        representant: clean(form.representant),
        // surface_ventilee_m2 = surface de l'op principale (champ surface désormais par op).
        batiment: clean({ ...form.batiment, surface_ventilee_m2: op0.surface ?? num(form.batiment.surface_ventilee_m2), nb_logements: num(form.batiment.nb_logements) }),
        // technique : type_vmc dérivé des fiches des ops ; nb_caissons = celui de l'op principale.
        technique: clean({ ...form.technique, type_vmc: vmcTypes, nb_caissons: op0.nb_caissons ?? null }),
        // cee : dérivé de l'op principale + délégataire GLOBAL + zone DOSSIER (compat devis/templates).
        cee: clean({
          ...form.cee,
          code_fiche: op0.fiche_ref || null,
          secteur: op0.secteur || null,
          operation_cee: ficheNom(op0.fiche_ref) || form.cee.operation_cee || null,
          zone_climatique: zoneDeduite || form.cee.zone_climatique || null,
          delegataire_nom: form.cee.delegataire_nom || null,
          montant_prime_cee: num(form.cee.montant_prime_cee),
          volume_kwh_cumac: num(form.cee.volume_kwh_cumac),
        }),
        operations: ops,
      };
      // CONTEXTE PROSPECT : l'enregistrement est délégué au parent (POST/PATCH
      // /prospects ou endpoint public). Le composant ne PATCHe PAS un dossier.
      if (onSubmit) {
        // 2e arg = fichier formulaire importé (peut être null) : le parent
        // l'attache au prospect après création/MAJ (POST attach-form). Les
        // appelants qui l'ignorent ne sont pas affectés (arg surnuméraire JS).
        const ok = await onSubmit(body, importedFile);
        if (ok) onUpdate?.();
        return;
      }
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": tenantId || "omv_france",
          Authorization: `Bearer ${localStorage.getItem("session_token")}`,
        },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.ok) { toast.success("Champs CEE enregistrés"); onUpdate?.(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e?.detail || "Erreur enregistrement CEE"); }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const cardCls = "rounded-lg p-4";
  const cardStyle = { background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" };

  return (
    <div className="space-y-4" data-testid="omv-cee-editor">
      {/* STRUCTURE EN 2 PARTIES (partagée dossier + prospect) :
          Partie 1 « Bénéficiaire & Site » (FIXE) = entreprise + représentant + bâtiment.
          Partie 2 « Opération » (V1 = VMC) = opérations CEE — swappable V2 sans toucher P1. */}
      <h2 className="text-sm font-medium pb-1" style={{ borderBottom: "2px solid var(--oh-stat-blue-border)", color: "var(--oh-text)" }}>
        Partie 1 — Bénéficiaire &amp; Site
      </h2>

      {/* Import formulaire CEE — extraction PURE (pré-remplit ci-dessous, aucune
          écriture). Caché en mode public (pas de session → endpoint 401) et en lecture seule. */}
      {!publicMode && !readOnly && (
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            className="hidden"
            onChange={(e) => handleImportForm(e.target.files?.[0])}
            data-testid="omv-cee-import-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
            data-testid="omv-cee-import-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import formulaire CEE
          </Button>
          <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
            PDF/JPEG/PNG — pré-remplit les champs ci-dessous (à vérifier). Aucune création de dossier.
          </span>
        </div>
      )}

      {/* APERÇU "à confirmer" des champs importés — INDÉPENDANT du rendu conditionnel
          (visible même si un champ ci-dessous est masqué). */}
      {!publicMode && !readOnly && importPreview && importPreview.length > 0 && (
        <div className="rounded p-3" data-testid="omv-cee-import-preview"
          style={{ background: "var(--oh-stat-blue-bg)", border: "1px solid var(--oh-stat-blue-border)" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium" style={{ color: "var(--oh-stat-blue-text)" }}>
              Importé du formulaire ({importPreview.length} champ{importPreview.length > 1 ? "s" : ""}) — à vérifier
            </span>
            <button type="button" onClick={() => setImportPreview(null)} className="text-xs underline"
              style={{ color: "var(--oh-text-muted)" }} data-testid="omv-cee-import-preview-close">Masquer</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
            {importPreview.map((p, i) => (
              <div key={i} className="text-[11px] truncate" style={{ color: "var(--oh-text)" }}>
                <span style={{ color: "var(--oh-text-muted)" }}>{p.label} : </span>{p.value}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={cardCls} style={cardStyle}>
        <h3 className="text-sm font-medium mb-3" style={{ color: "var(--oh-text)" }}>Entreprise bénéficiaire</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* SIRET/SIREN EN TÊTE + recherche SIRENE (auto-remplit l'entreprise + le
              site si vides). SIREN dérivé affiché. Avertissement doublon non bloquant. */}
          <div className="sm:col-span-2">
            <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>
              SIRET / SIREN {sirenDerive ? <span className="opacity-70">· SIREN {sirenDerive}</span> : null}
            </label>
            <div className="relative">
              <input value={form.entreprise.siret ?? ""} onChange={(e) => onSiretChange(e.target.value)}
                placeholder="14 chiffres (SIRET) ou 9 (SIREN) → recherche automatique"
                className="w-full rounded px-3 py-2 pr-9 text-sm"
                aria-invalid={!!siretMsg}
                data-testid="omv-cee-siret"
                style={{ background: "var(--oh-bg-surface)", border: `1px solid ${siretMsg ? "var(--oh-stat-red-border)" : "var(--oh-border)"}`, color: "var(--oh-text)" }} />
              <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 opacity-50" />
            </div>
            {siretMsg && <p className="text-xs mt-1" style={{ color: "var(--oh-stat-red-text)" }}>{siretMsg}</p>}
            {!siretMsg && siretInfo && siretInfo.message && (
              <p className="text-xs mt-1" style={{ color: siretInfo.found ? "var(--oh-stat-green-text)" : "var(--oh-stat-amber-text)" }}>{siretInfo.message}</p>
            )}
            {/* DÉDUP SÉPARÉE PAR MONDE (règle JJ) :
                - DOSSIER/CLIENT existant = simple INFO discrète (entonnoir
                  prospect→conversion→dossier), JAMAIS le mot « doublon ».
                - PROSPECT existant (même SIRET) = vrai doublon de formulaire →
                  note ; le choix « Créer / Utiliser le prospect » est proposé à
                  l'enregistrement (ProspectsStagingGrid). */}
            {dupWarn?.dossier && (
              <p className="text-xs mt-1" style={{ color: "var(--oh-text-muted)" }} data-testid="omv-cee-dossier-info">
                ℹ️ Cette entreprise a déjà un {dupWarn.dossier.kind === "client" ? "client" : "dossier"} OMV
                {dupWarn.dossier.soft ? " (même groupe SIREN)" : ""}{dupWarn.dossier.masked_ref ? ` — réf. ${dupWarn.dossier.masked_ref}` : ""}. Saisie possible.
              </p>
            )}
            {dupWarn?.prospect && (
              <p className="text-xs mt-1"
                 style={{ color: dupWarn.prospect.soft ? "var(--oh-text-muted)" : "var(--oh-stat-amber-text)" }}
                 data-testid="omv-cee-prospect-dup">
                {dupWarn.prospect.soft
                  ? "ℹ️ Un prospect d'un établissement du même groupe (SIREN) existe déjà. Saisie possible."
                  : "⚠️ Un prospect avec ce SIRET existe déjà — à l'enregistrement, vous pourrez l'ouvrir ou en créer un nouveau."}
              </p>
            )}
          </div>
          <Field label="Raison sociale" value={form.entreprise.raison_sociale} onChange={(v) => setF("entreprise", "raison_sociale", v)} />
          <SelectField label="Secteur d'activité (bénéficiaire)" value={form.entreprise.secteur_activite}
            onChange={(v) => setF("entreprise", "secteur_activite", v)}
            options={(secteursActivite || []).map((s) => ({ value: s.id ?? s.value ?? s.label, label: s.label ?? s.id }))} />
          {/* Adresse bénéficiaire via BAN : rue seule ici, CP/Ville découpés. */}
          <div className="sm:col-span-2">
            <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Adresse (rue)</label>
            <BanAddressInput value={form.entreprise.adresse} testId="omv-cee-adresse"
              onChange={(v) => setF("entreprise", "adresse", v)}
              onPick={({ rue, cp, ville }) => setForm((f) => ({ ...f, entreprise: { ...f.entreprise, adresse: rue, cp: cp || f.entreprise.cp, ville: ville || f.entreprise.ville } }))} />
          </div>
          <Field label="Code postal" value={form.entreprise.cp} onChange={(v) => setF("entreprise", "cp", v)} />
          <Field label="Ville" value={form.entreprise.ville} onChange={(v) => setF("entreprise", "ville", v)} />
        </div>
      </div>

      <div className={cardCls} style={cardStyle}>
        <h3 className="text-sm font-medium mb-3" style={{ color: "var(--oh-text)" }}>Représentant</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Nom" value={form.representant.nom} onChange={(v) => setF("representant", "nom", v)} />
          <Field label="Prénom" value={form.representant.prenom} onChange={(v) => setF("representant", "prenom", v)} />
          <Field label="Fonction" value={form.representant.fonction} onChange={(v) => setF("representant", "fonction", v)} />
          <Field label="Email" type="email" value={form.representant.email} onChange={(v) => setF("representant", "email", v)} />
          <Field label="Téléphone" value={form.representant.tel} onChange={(v) => setF("representant", "tel", v)} />
          <Field label="Portable" value={form.representant.portable} onChange={(v) => setF("representant", "portable", v)} />
        </div>
      </div>

      <div className={cardCls} style={cardStyle}>
        <h3 className="text-sm font-medium mb-3" style={{ color: "var(--oh-text)" }}>Bâtiment</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* GAUCHE — localisation du chantier : Établissement → Adresse → Ville → CP → Zone */}
          <div className="space-y-3">
            <Field label="Nom établissement (chantier)" value={form.batiment.nom_etablissement} onChange={(v) => setF("batiment", "nom_etablissement", v)} />
            {/* Adresse travaux via BAN : rue seule + CP/Ville découpés dans leurs champs. */}
            <div>
              <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Adresse des travaux (rue)</label>
              <BanAddressInput value={form.batiment.adresse_travaux} testId="omv-cee-adresse-travaux"
                onChange={(v) => setF("batiment", "adresse_travaux", v)}
                onPick={({ rue, cp, ville }) => setForm((f) => ({ ...f, batiment: { ...f.batiment, adresse_travaux: rue, cp_travaux: cp || f.batiment.cp_travaux, ville_travaux: ville || f.batiment.ville_travaux } }))} />
            </div>
            <Field label="Ville" value={form.batiment.ville_travaux} onChange={(v) => setF("batiment", "ville_travaux", v)} />
            {/* CP et Zone climatique sur la MÊME ligne (côte à côte). Zone = lecture seule,
                CALCULÉE depuis l'adresse (travaux prioritaire, sinon principale) — sert au cumac. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="CP (→ zone)" value={form.batiment.cp_travaux} onChange={(v) => setF("batiment", "cp_travaux", v)} />
              <div>
                <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Zone climatique (auto)</label>
                <input readOnly
                  value={zoneDeduite ? `${zoneDeduite}${zoneSource ? ` — ${zoneSource}` : ""}` : (cpForZone ? "—" : "renseigner une adresse/CP")}
                  className="w-full rounded px-3 py-2 text-sm"
                  style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}
                  data-testid="cee-zone-auto" />
              </div>
            </div>
          </div>
          {/* DROITE — caractéristiques bâtiment : Type → Surface → Nb logements → +2 ans */}
          <div className="space-y-3">
            <SelectField label="Type de bâtiment" value={form.batiment.type_batiment} onChange={(v) => setF("batiment", "type_batiment", v)}
              options={[{ value: "residentiel_collectif", label: "Résidentiel collectif" }, { value: "enseignement", label: "Enseignement" }, { value: "autre", label: "Autre" }]} />
            {/* Surface : déplacée AU NIVEAU OPÉRATION (CC-DEV1-26) — chaque opération porte sa surface. */}
            <Field label="Nombre de logements" type="number" value={form.batiment.nb_logements} onChange={(v) => setF("batiment", "nb_logements", v)} />
            <label className="flex items-center gap-2 text-sm pt-6" style={{ color: "var(--oh-text)" }}>
              <input type="checkbox" checked={!!form.batiment.plus_2_ans} onChange={(e) => setF("batiment", "plus_2_ans", e.target.checked)} />
              Bâtiment de plus de 2 ans
            </label>
          </div>
        </div>
      </div>

      {/* ── Opérations CEE (ONGLETS) — multi-opération (CC-DEV1-24).
          • Onglet 1 = opération PRINCIPALE = champs cee/technique du dossier (save cee INCHANGÉ).
          • Onglets 2+ = opérations SUPPLÉMENTAIRES (fiche+secteur+surface) → dossier.operations[].
          cumac/prime affichés = CALCULÉS PAR LE SERVEUR ; total = somme des opérations. */}
      <h2 className="text-sm font-medium pb-1 pt-2" style={{ borderBottom: "2px solid var(--oh-stat-blue-border)", color: "var(--oh-text)" }}>
        Partie 2 — Opération (VMC)
      </h2>
      <div className={cardCls} style={cardStyle}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Opérations CEE</h3>
          {opsCalc?.totals && (
            <span className="text-xs rounded px-2 py-1" data-testid="cee-ops-total"
              style={{ background: "var(--oh-stat-green-bg)", border: "1px solid var(--oh-stat-green-border)", color: "var(--oh-stat-green-text)" }}>
              Total {opsCalc.totals.nb_operations} op. : {fmtKwh(opsCalc.totals.total_cumac)}{opsCalc.totals.total_prime ? ` · ${fmtEur(opsCalc.totals.total_prime)}` : ""}
            </span>
          )}
        </div>

        {/* NIVEAU GLOBAL « Opération CEE » : Délégataire (commun à toutes) + Zone (dossier). */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <SelectField label="Délégataire (commun à toutes les opérations)" value={form.cee.delegataire_nom || ""} onChange={onSelectDeleg}
            options={(delegataires || []).map((d) => ({ value: d.nom, label: d.nom + (d.siren ? ` (${d.siren})` : "") }))} />
          <div>
            <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Zone climatique (dossier — adresse travaux)</label>
            <input readOnly data-testid="cee-zone-dossier"
              value={zoneDeduite ? `${zoneDeduite}${zoneSource ? ` — ${zoneSource}` : ""}` : (cpForZone ? "—" : "renseigner une adresse/CP")}
              className="w-full rounded px-3 py-2 text-sm"
              style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }} />
          </div>
        </div>

        {/* PROSPECT uniquement : Type de VMC (régie) → pilote la création des opérations. */}
        {isProspect && (
          <div className="rounded p-3 mb-4" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }} data-testid="cee-vmc-prospect">
            <div className="text-xs mb-2" style={{ color: "var(--oh-text-muted)" }}>Type de VMC (coché par la régie) — crée l'opération correspondante</div>
            <div className="flex flex-wrap gap-2">
              {[["simple_flux", "Simple flux (BAT-TH-125)"], ["double_flux", "Double flux (BAT-TH-126)"]].map(([type, label]) => {
                const on = operations.some((o) => o.fiche_ref === VMC_FOR_TYPE[type]);
                return (
                  <button type="button" key={type} onClick={() => toggleVmcGlobal(type)} data-testid={`cee-vmc-${type}`}
                    className="rounded px-3 py-2 text-sm" style={{
                      background: on ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)",
                      border: `1px solid ${on ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`,
                      color: on ? "var(--oh-stat-blue-text)" : "var(--oh-text)",
                    }}>{label}{on ? " ✓" : ""}</button>
                );
              })}
            </div>
          </div>
        )}

        {/* Barre d'onglets : une opération par onglet + bouton ajouter. */}
        <div className="flex flex-wrap items-center gap-2 mb-4 pb-2" style={{ borderBottom: "1px solid var(--oh-border)" }}>
          {operations.map((_, i) => {
            const on = activeOp === i;
            return (
              <button type="button" key={i} onClick={() => setActiveOp(i)} data-testid={`cee-op-tab-${i}`}
                className="rounded px-3 py-1.5 text-sm"
                style={{
                  background: on ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)",
                  border: `1px solid ${on ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`,
                  color: on ? "var(--oh-stat-blue-text)" : "var(--oh-text)",
                }}>
                Opération {i + 1}
              </button>
            );
          })}
          <button type="button" onClick={addOp} data-testid="cee-op-add"
            className="rounded px-3 py-1.5 text-sm"
            style={{ background: "var(--oh-bg-surface)", border: "1px dashed var(--oh-border)", color: "var(--oh-text-muted)" }}>
            + Ajouter une opération
          </button>
        </div>

        {/* Contenu de l'onglet actif — STRUCTURE UNIFORME : fiche + secteur + surface (+ caissons si VMC). */}
        {(() => {
          const idx = Math.min(activeOp, operations.length - 1);
          const op = operations[idx] || {};
          const calc = opsCalc?.operations?.[idx];
          const prods = produitsForFiche(op.fiche_ref);   // produits liés à la fiche de l'op
          const mode = op.prix_mode || "cee";
          return (
            <div className="space-y-3">
              <OperationFields
                codeFiche={op.fiche_ref} onFiche={(v) => setOp(idx, "fiche_ref", v)}
                secteur={op.secteur} onSecteur={(v) => setOp(idx, "secteur", v)}
                surface={op.surface} onSurface={(v) => setOp(idx, "surface", v)}
                nbCaissons={op.nb_caissons} onNbCaissons={(v) => setOp(idx, "nb_caissons", v)}
                ficheOptions={ficheOptionsFor(op.fiche_ref)}
                secteurOptions={secteurOptionsForOp(op)} />

              {/* 🔑 N°980 — commentaire PROPRE À CE DEVIS pour la ligne de cette opération
                  (ex. « bâtiment B — salle de réception »). ⛔ N'écrit pas le produit. */}
              {op.fiche_ref && (
                <div className="mb-3">
                  <label htmlFor={`cee-op-commentaire-${idx}`} className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>
                    Commentaire de la ligne de devis (propre à ce dossier)
                  </label>
                  <input
                    id={`cee-op-commentaire-${idx}`}
                    type="text" maxLength={1000}
                    value={op.commentaire_ligne || ""}
                    onChange={(e) => setOp(idx, "commentaire_ligne", e.target.value)}
                    placeholder="Ex. bâtiment B — salle de réception"
                    data-testid={`cee-op-commentaire-${idx}`}
                    className="w-full rounded px-3 py-2 text-sm"
                    style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                  />
                </div>
              )}

              {/* Liaison PRODUIT (CC-DEV1-28) : liste FILTRÉE par fiche (Produit.fiche_cee == fiche op). */}
              {op.fiche_ref && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`cee-op-produit-${idx}`}>
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Produit (lié à la fiche {op.fiche_ref})</label>
                    {prods.length > 0 ? (
                      <select value={op.produit_id || ""}
                        onChange={(e) => {
                          const p = prods.find((x) => (x.id || x._id) === e.target.value);
                          setOperations((o) => o.map((x, k) => k === idx ? { ...x, produit_id: e.target.value, produit_ref: (p?.reference || "") } : x));
                        }}
                        data-testid={`cee-op-produit-select-${idx}`}
                        className="w-full rounded px-3 py-2 text-sm"
                        style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}>
                        <option value="">— Choisir un produit —</option>
                        {prods.map((p) => (
                          <option key={p.id || p._id} value={p.id || p._id}>
                            {p.designation}{p.prix_ht != null ? ` — ${Number(p.prix_ht).toLocaleString("fr-FR")} € HT/${p.unite || "u"}` : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs rounded px-3 py-2" data-testid={`cee-op-produit-empty-${idx}`}
                        style={{ background: "var(--oh-stat-amber-bg)", border: "1px solid var(--oh-stat-amber-border)", color: "var(--oh-stat-amber-text)" }}>
                        Aucun produit lié à cette fiche — créez-en un au catalogue (champ « Fiche CEE » = {op.fiche_ref}).
                      </p>
                    )}
                  </div>
                  {/* Mode de PRIX (Solène choisit) — défaut « Prix CEE » (reste à charge 0). */}
                  <div>
                    <label className="text-xs mb-1 block" style={{ color: "var(--oh-text-muted)" }}>Mode de prix</label>
                    <div className="flex gap-2">
                      {[["cee", "Prix CEE (reste 0)"], ["catalogue", "Prix catalogue"]].map(([m, label]) => {
                        const on = mode === m;
                        const disabled = m === "catalogue" && !op.produit_id;
                        return (
                          <button type="button" key={m} disabled={disabled}
                            onClick={() => setOp(idx, "prix_mode", m)} data-testid={`cee-op-mode-${m}-${idx}`}
                            className="rounded px-3 py-2 text-sm" style={{
                              opacity: disabled ? 0.4 : 1,
                              background: on ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)",
                              border: `1px solid ${on ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`,
                              color: on ? "var(--oh-stat-blue-text)" : "var(--oh-text)",
                            }}>{label}{on ? " ✓" : ""}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between text-xs" data-testid={`cee-op-calc-${idx}`}>
                <div className="flex gap-4" style={{ color: "var(--oh-text-muted)" }}>
                  <span>Cumac : <b style={{ color: "var(--oh-text)" }}>{fmtKwh(calc?.cumac)}</b></span>
                  <span>Prime : <b style={{ color: "var(--oh-text)" }}>{fmtEur(calc?.prime)}</b></span>
                </div>
                {operations.length > 1 && (
                  <button type="button" onClick={() => removeOp(idx)} className="underline" style={{ color: "var(--oh-stat-red-text)" }} data-testid="cee-op-remove">Retirer l'opération</button>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {!readOnly && (
        <div className="flex flex-col items-end gap-1">
          {validationMsg && (
            <span className="text-xs" data-testid="cee-validation-msg" style={{ color: "var(--oh-stat-amber-text)" }}>{validationMsg}</span>
          )}
          <Button onClick={save} disabled={saving || !allOpsComplete} className="bg-orange-600 hover:bg-orange-700" data-testid="omv-cee-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {submitLabel || "Enregistrer les champs CEE"}
          </Button>
        </div>
      )}
    </div>
  );
}
